import type { ReactNode } from 'react';
import {
  Alert,
  Badge,
  Button,
  Checklist,
  SideDrawer,
  SummaryCell,
  SummaryStrip,
  linkTextClasses,
  type ChecklistItem,
} from '@evogard/ui';
import type { ClaimSummary, CompensationSummary } from '@evogard/contract';
import { format, formatInstant, fromColumn, type Money } from '@evogard/domain';
import {
  claimFileUrl,
  useCompensationValidation,
  type useCompensationSummary,
} from './useDesk.js';
import { Section, Wait } from './RepairBlock.js';
import { CompensationDebts, ExpenseStageTag, debtName } from './CompensationDebts.js';

type SummaryQuery = ReturnType<typeof useCompensationSummary>;

/**
 * O bloco do alto da mesa da Indenização, e a gaveta dele (09/10/2026) — a
 * única parte da mesa que é diferente da Análise e do Reparo. Onde a Análise
 * mostra o ocorrido e o Reparo a oficina, a Indenização mostra a conta, o prazo
 * e o checklist de documentos. **O checklist mora aqui, e não na coluna da
 * esquerda** (decisão de 09/10/2026): a da esquerda continua sendo a dos
 * envolvidos, igual nos três departamentos.
 *
 * Nada aqui se digita: é o que a API lê de uma vez
 * (`GET /claims/:id/compensation-summary`). O valor da indenização nunca é
 * gravado — é a conta, refeita a cada leitura. O que falta aparece como
 * espera, dizendo de onde vem: a tela não inventa valor nem data.
 */
export function CompensationSummaryStrip({
  summary,
  open,
  onToggle,
}: {
  summary: SummaryQuery;
  open: boolean;
  onToggle: () => void;
}) {
  const action = {
    label: open ? 'Fechar conta e documentos' : 'Ver conta e documentos',
    onClick: onToggle,
    expanded: open,
  };
  if (summary.isError) {
    return (
      <SummaryStrip label="Conta da indenização" action={action}>
        <SummaryCell
          value="Não foi possível ler a conta."
          detail={summary.error.message}
        />
      </SummaryStrip>
    );
  }
  const s = summary.data;
  if (!s) {
    return (
      <SummaryStrip label="Conta da indenização" action={action}>
        <SummaryCell value="Carregando a conta…" />
      </SummaryStrip>
    );
  }
  const fipe = money(s.fipe.value);
  const deducted = money(s.account.deducted)!;
  const net = money(s.account.net);
  const counted = s.deductions.filter((d) => d.counts).length;
  const event = s.theft ? 'furto ou roubo' : 'perda total';
  return (
    <SummaryStrip label="Conta da indenização" action={action}>
      <SummaryCell
        value={
          fipe !== null ? (
            <span className="tabular-nums">{format(fipe)}</span>
          ) : (
            'FIPE não informada'
          )
        }
        detail={
          fipe !== null
            ? `FIPE${s.fipe.referenceDate ? ` em ${ddmm(s.fipe.referenceDate)}` : ''} · ${event}`
            : 'vem da Entrada da Análise'
        }
        noShrink
      />
      <SummaryCell
        value={<span className="tabular-nums text-erro">− {format(deducted)}</span>}
        detail={
          counted === 0
            ? 'nenhuma dedução ainda'
            : `PI e débitos · ${counted === 1 ? '1 dedução' : `${counted} deduções`}`
        }
        noShrink
      />
      <SummaryCell
        value={
          net !== null ? <span className="tabular-nums">{format(net)}</span> : 'Sem conta'
        }
        detail={net !== null ? 'a receber hoje' : 'falta a FIPE'}
        noShrink
      />
      <DeadlineCell
        deadlines={s.deadlines}
        theft={s.theft}
        hasFipe={s.fipe.value !== null}
      />
      <SummaryCell
        value={
          <span className="tabular-nums">
            Documentos {s.checklist.validated} de {s.checklist.required}
          </span>
        }
        detail={documentsDetail(s.checklist)}
        noShrink
      />
    </SummaryStrip>
  );
}

/**
 * O prazo: a data limite com o selo, ou "não começou" até a documentação ficar
 * completa. Sem FIPE não há faixa, e a entrega completa não conta prazo nenhum
 * (`compensationDeadlines`) — dizer "não começou" ali seria prometer um relógio.
 */
function DeadlineCell({
  deadlines,
  theft,
  hasFipe,
}: {
  deadlines: CompensationSummary['deadlines'];
  theft: boolean;
  hasFipe: boolean;
}) {
  if (!deadlines.dueAt) {
    return hasFipe ? (
      <SummaryCell value="Prazo não começou" detail="conta da entrega completa" />
    ) : (
      <SummaryCell value="Sem prazo" detail="falta a FIPE" />
    );
  }
  const due = localDay(deadlines.dueAt);
  return (
    <SummaryCell
      value={<span className="tabular-nums">Limite {ddmm(due)}</span>}
      detail={
        <span className="inline-flex items-center gap-1.5">
          <DaysBadge on={due} />
          {deadlines.targetAt
            ? `meta ${ddmm(localDay(deadlines.targetAt))}`
            : theft
              ? ''
              : 'sem meta interna'}
        </span>
      }
      title={`Data limite: ${formatInstant(deadlines.dueAt)}`}
      noShrink
    />
  );
}

/**
 * A gaveta da conta e dos documentos: a conta por extenso — FIPE, cada
 * dedução e a quem se paga, o líquido —, os débitos, que se editam aqui no
 * Pagamento de Débito, o prazo, e o checklist, onde cada documento recebido se
 * confere.
 */
export function CompensationDrawer({
  claim,
  summary,
  canEdit,
  onClose,
}: {
  claim: ClaimSummary;
  summary: SummaryQuery;
  /**
   * Pode conferir documentos e editar débitos: tem a permissão de mover a
   * Indenização e o sinistro não está arquivado. A fase decide o resto.
   */
  canEdit: boolean;
  onClose: () => void;
}) {
  const s = summary.data;
  return (
    <SideDrawer
      eyebrow={`Indenização do aviso #${claim.id}`}
      title="A conta, o prazo e os documentos"
      closeLabel="Fechar a conta"
      onClose={onClose}
    >
      {summary.isError && <Alert tone="erro">{summary.error.message}</Alert>}
      {!s && !summary.isError && (
        <p className="text-sm text-texto-suave">Carregando a conta…</p>
      )}
      {s && (
        <>
          <Account summary={s} />
          <CompensationDebts claimId={claim.id} summary={s} />
          <Deadlines summary={s} />
          <Documents
            claimId={claim.id}
            summary={s}
            canValidate={canEdit && s.checklistEditable}
          />
        </>
      )}
    </SideDrawer>
  );
}

/** "Indenização = FIPE − PI − débitos", uma linha por dedução. */
function Account({ summary: s }: { summary: CompensationSummary }) {
  const fipe = money(s.fipe.value);
  const net = money(s.account.net);
  return (
    <Section title="Indenização = FIPE − PI − débitos" aside="calculada, nunca gravada">
      <div className="flex flex-col rounded-borda border border-borda px-3">
        <Line
          label={`FIPE${s.fipe.referenceDate ? ` em ${ddmmyyyy(s.fipe.referenceDate)}` : ''}`}
          value={fipe !== null ? format(fipe) : null}
          strong
        />
        {s.deductions.map((d) => (
          <Line
            key={d.id}
            label={debtName(d)}
            tag={payTag(d)}
            value={`− ${format(money(d.amount)!)}`}
            off={!d.counts}
          />
        ))}
        <Line label="A receber hoje" value={net !== null ? format(net) : null} total />
      </div>
      {fipe === null && (
        <Wait>A FIPE é gravada na Entrada da Análise. Sem ela, a conta não fecha.</Wait>
      )}
    </Section>
  );
}

function Line({
  label,
  value,
  tag,
  strong = false,
  total = false,
  off = false,
}: {
  label: string;
  value: string | null;
  tag?: ReactNode;
  strong?: boolean;
  total?: boolean;
  off?: boolean;
}) {
  return (
    <div
      className={[
        'grid grid-cols-[minmax(0,1fr)_auto_auto] items-center gap-x-2.5 py-2 text-sm',
        total
          ? 'mt-0.5 border-t-2 border-texto font-semibold'
          : 'border-b border-dashed border-borda',
        off ? 'text-texto-tenue line-through' : '',
      ].join(' ')}
    >
      <span className={strong ? 'min-w-0 font-semibold' : 'min-w-0'}>{label}</span>
      <span>{tag}</span>
      <span className="min-w-24 text-right tabular-nums font-semibold">
        {value ?? '—'}
      </span>
    </div>
  );
}

/** O prazo, contado da entrega completa — a meta interna só a equipe vê. */
function Deadlines({ summary: s }: { summary: CompensationSummary }) {
  const d = s.deadlines;
  return (
    <Section title="O prazo">
      {d.dueAt ? (
        <dl className="grid grid-cols-2 gap-x-4 gap-y-3 text-sm">
          {d.targetAt && (
            <div>
              <dt className="text-texto-suave">Meta interna</dt>
              <dd className="flex items-center gap-1.5 tabular-nums">
                {ddmmyyyy(localDay(d.targetAt))} <DaysBadge on={localDay(d.targetAt)} />
              </dd>
            </div>
          )}
          <div>
            <dt className="text-texto-suave">Data limite</dt>
            <dd className="flex items-center gap-1.5 tabular-nums">
              {ddmmyyyy(localDay(d.dueAt))} <DaysBadge on={localDay(d.dueAt)} />
            </dd>
          </div>
        </dl>
      ) : s.fipe.value === null ? (
        <Wait>
          Sem a FIPE não há prazo: a data limite depende da faixa — 90 dias até R$ 70 mil,
          120 acima. A entrega completa sem FIPE não conta prazo nenhum.
        </Wait>
      ) : (
        <Wait>
          O relógio começa quando a documentação fica completa, na Aguardando
          Documentação: todo documento exigido conferido.
        </Wait>
      )}
      {d.targetAt && (
        <p className="text-xs text-texto-suave">
          A meta interna não é comunicada ao associado; só a equipe vê.
        </p>
      )}
    </Section>
  );
}

/**
 * O checklist do evento, com os quatro estados — validado, recebido (falta
 * conferir), falta, não se aplica —, e, por baixo, os arquivos de cada
 * documento, com "Conferir" e "Desfazer conferência". Arquivo novo entra pelo
 * campo dele no formulário da Aguardando Documentação, e não por aqui.
 */
function Documents({
  claimId,
  summary: s,
  canValidate,
}: {
  claimId: number;
  summary: CompensationSummary;
  canValidate: boolean;
}) {
  const validation = useCompensationValidation(claimId);
  const items: ChecklistItem[] = s.checklist.items.map((item) => ({
    key: item.kind,
    label: item.label,
    status:
      item.state === 'validado'
        ? 'done'
        : item.state === 'recebido'
          ? 'review'
          : item.state === 'nao_se_aplica'
            ? 'skipped'
            : 'pending',
    detail:
      item.state === 'validado'
        ? 'validado'
        : item.state === 'recebido'
          ? 'recebido · falta conferir'
          : item.state === 'nao_se_aplica'
            ? 'não se aplica a este sinistro'
            : 'falta',
  }));
  const files = s.checklist.items.flatMap((item) =>
    item.files.map((f) => ({ ...f, label: item.label })),
  );
  return (
    <Section
      title="Documentos"
      aside={s.checklistEditable ? 'confere-se nesta fase' : 'só leitura'}
    >
      <Checklist
        title={`Checklist ${s.theft ? 'do furto ou roubo' : 'da perda total'}`}
        items={items}
      />
      {validation.isError && <Alert tone="erro">{validation.error.message}</Alert>}
      {files.length > 0 ? (
        <ul className="flex flex-col rounded-borda border border-borda">
          {files.map((f) => (
            <li
              key={f.id}
              className="flex flex-wrap items-center gap-x-3 gap-y-1 border-b border-borda px-3 py-2 last:border-b-0"
            >
              <span className="flex min-w-0 flex-1 flex-col">
                <span className="text-sm font-semibold">{f.label}</span>
                <button
                  type="button"
                  className={linkTextClasses('self-start truncate text-left text-xs')}
                  onClick={() =>
                    void claimFileUrl(claimId, f.id).then((url) =>
                      window.open(url, '_blank', 'noopener'),
                    )
                  }
                >
                  {f.fileName}
                </button>
                {f.validatedAt && (
                  <span className="text-xs text-texto-suave">
                    Conferido {formatInstant(f.validatedAt)}
                    {f.validatedByName ? ` por ${f.validatedByName}` : ''}
                  </span>
                )}
              </span>
              {f.validatedAt ? (
                <Badge tone="sucesso">validado</Badge>
              ) : (
                <Badge tone="alerta">a conferir</Badge>
              )}
              {canValidate && (
                <Button
                  size="sm"
                  variant={f.validatedAt ? 'secundario' : 'contorno'}
                  loading={
                    validation.isPending && validation.variables?.documentId === f.id
                  }
                  disabled={validation.isPending}
                  onClick={() =>
                    validation.mutate({
                      documentId: f.id,
                      validated: f.validatedAt === null,
                    })
                  }
                >
                  {f.validatedAt ? 'Desfazer conferência' : 'Conferir'}
                </Button>
              )}
            </li>
          ))}
        </ul>
      ) : (
        <Wait>
          Nenhum documento chegou. Eles entram pelos campos do formulário da Aguardando
          Documentação.
        </Wait>
      )}
    </Section>
  );
}

/* ------------------------------------------------------------------------- *
 * O texto de cada coisa
 * ------------------------------------------------------------------------- */

/** A quem se paga, ou se só desconta — e o que não desconta, e por quê. */
function payTag(d: CompensationSummary['deductions'][number]) {
  /* O débito pago a um credor é uma despesa (29/10/2026): a fase dela diz tudo. */
  if (d.expense) return <ExpenseStageTag stage={d.expense.stage} />;
  if (d.status === 'pago_pelo_associado')
    return <Badge tone="informacao">pago pelo associado</Badge>;
  if (d.status === 'dispensado') return <Badge tone="informacao">dispensado</Badge>;
  if (d.status === 'retido') return <Badge tone="alerta">retida · fora da conta</Badge>;
  if (!d.creditorName) return <Badge tone="informacao">só desconta</Badge>;
  return (
    <span className="inline-flex items-center gap-1">
      <Badge tone="informacao">pagar a {d.creditorName}</Badge>
      {d.receipts === 0 && <Badge tone="erro">sem comprovante</Badge>}
    </span>
  );
}

function documentsDetail(c: CompensationSummary['checklist']): string {
  if (c.validated === c.required) return 'todos conferidos';
  const toCheck = c.received - c.validated;
  const missing = c.required - c.received;
  return [
    missing > 0 ? (missing === 1 ? '1 falta' : `${missing} faltam`) : null,
    toCheck > 0 ? `${toCheck} a conferir` : null,
  ]
    .filter(Boolean)
    .join(' · ');
}

/** O selo de dias até uma data: no prazo, perto, ou vencido. */
function DaysBadge({ on }: { on: string }) {
  const days = daysTo(on);
  if (days === null) return null;
  if (days < 0) {
    return (
      <Badge tone="erro">vencido há {-days === 1 ? '1 dia' : `${-days} dias`}</Badge>
    );
  }
  if (days <= 10) {
    return <Badge tone="alerta">faltam {days === 1 ? '1 dia' : `${days} dias`}</Badge>;
  }
  return <Badge tone="sucesso">faltam {days} dias</Badge>;
}

const money = (value: string | null): Money | null =>
  value === null ? null : fromColumn(value);

/** Quantos dias faltam até a data (negativo: passou). */
function daysTo(iso: string): number | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!m) return null;
  const target = Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  const now = new Date();
  const today = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());
  return Math.round((target - today) / 86_400_000);
}

/** O dia de um instante, no fuso de quem vê. */
function localDay(instant: string): string {
  const d = new Date(instant);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

const ddmm = (iso: string) => `${iso.slice(8, 10)}/${iso.slice(5, 7)}`;
const ddmmyyyy = (iso: string) =>
  `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(0, 4)}`;
